'use client';
import { Button } from '@/components/ui/buttons';
import { Loading } from '@/components/ui/forms';
import { useGoogleMaps } from '@/hooks/useGoogleMaps';
import { useInitializeLocation } from '@/hooks/useInitializeLocation';
import useShippingInfoStore from '@/store/shipping-info.store';
import { GoogleMap, Marker, StandaloneSearchBox } from '@react-google-maps/api';
import { useTranslations } from 'next-intl';
import { useEffect, useRef, useState } from 'react';

const containerStyle = {
  width: '100%',
  height: '9.063rem',
};
const AddEditAddressMap = ({
  handleOnClose,
  handleSubmit,
  isLoading,
  selectedAddress,
}) => {
  const { openShippingInfoModal } = useShippingInfoStore();

  const lat = selectedAddress?.custom_attributes?.find(
    (e) => e?.attribute_code === 'lat'
  )?.value;
  const lng = selectedAddress?.custom_attributes?.find(
    (e) => e?.attribute_code === 'lng'
  )?.value;

  const [markerPosition, setMarkerPosition] = useState(
    lat && lng ? { lat: Number(lat), lng: Number(lng) } : null
  );
  const [mapCenter, setMapCenter] = useState(null);
  const [searchInput, setSearchInput] = useState('');
  const searchBoxRef = useRef(null);

  const [address, setAddress] = useState({
    lat: markerPosition?.lat || '',
    long: markerPosition?.lng || '',
    street: '',
  });
  const { isLoaded, loadError } = useGoogleMaps();

  useEffect(() => {
    if (markerPosition && searchInput) {
      setAddress({
        lat: markerPosition.lat,
        long: markerPosition.lng,
        street: searchInput,
      });
    }
  }, [markerPosition, searchInput]);

  const t = useTranslations('modals');

  const initialized = useInitializeLocation(
    openShippingInfoModal,
    selectedAddress,
    setSearchInput,
    setMarkerPosition,
    setMapCenter
  );
  const handleOnPlacesChanged = () => {
    const place = searchBoxRef.current?.getPlaces()?.[0];
    if (place?.geometry?.location) {
      const coords = {
        lat: place.geometry.location.lat(),
        lng: place.geometry.location.lng(),
      };

      setMarkerPosition(coords);
      setMapCenter(coords);

      const formattedAddress = place.formatted_address || '';
      setSearchInput(formattedAddress);

      setAddress({
        lat: coords.lat,
        long: coords.lng,
        street: formattedAddress,
      });
    }
  };

  const onMapClick = async (e) => {
    const clickedPosition = {
      lat: e.latLng.lat(),
      lng: e.latLng.lng(),
    };
    setMarkerPosition(clickedPosition);

    try {
      const geocoder = new window.google.maps.Geocoder();
      const response = await geocoder.geocode({ location: clickedPosition });
      if (response?.results?.[0]) {
        const address = response.results[0].formatted_address;
        const country =
          response.results[0].address_components?.find((c) =>
            c?.types?.includes('country')
          )?.long_name || '';
        setSearchInput(country ? `${address} (${country})` : address);
        setAddress({
          lat: e.latLng.lat(),
          long: e.latLng.lng(),
          street: address,
        });
      }
    } catch {
      // setSearchInput(
      //   `${clickedPosition.lat.toFixed(6)}, ${clickedPosition.lng.toFixed(6)}`
      // );
    }
  };

  return (
    <>
      {!initialized ? (
        <div className='flex h-40 items-center justify-center'>
          <Loading />
        </div>
      ) : (
        <div className='p-4'>
          {isLoaded && !loadError && (
            <div className='mt-4'>
              <GoogleMap
                mapContainerStyle={containerStyle}
                center={mapCenter}
                zoom={14}
                onClick={onMapClick}
                options={{
                  streetViewControl: false,
                  mapTypeControl: false,
                  fullscreenControl: false,
                }}
              >
                <Marker position={markerPosition} />
              </GoogleMap>

              <label className='mt-[23px] mb-[9px] block'>
                {t('Suggested Address')}
              </label>

              <StandaloneSearchBox
                onLoad={(ref) => (searchBoxRef.current = ref)}
                onPlacesChanged={handleOnPlacesChanged}
              >
                <input
                  type='text'
                  placeholder={t('Search for location')}
                  value={searchInput}
                  onChange={(e) => setSearchInput(e.target.value)}
                  className='focus:border-primary mb-4 w-full rounded border px-3 py-2 shadow-sm focus:ring focus:outline-none'
                />
              </StandaloneSearchBox>
            </div>
          )}

          <div className='mt-[17px] mb-[21px] flex items-center justify-between gap-2'>
            <Button
              onClick={handleOnClose}
              className='flex h-[45px] w-[9.439rem] items-center justify-center rounded-[5px] border border-[#BBBBBB] bg-gradient-to-b from-[#F7F8FA] to-[#E7E9EC] text-[15px] font-bold !text-black shadow-[inset_0_1px_0_1px_#FFFFFF4D]'
            >
              {t('Cancel')}
            </Button>
            <Button
              onClick={() => handleSubmit(address)}
              disabled={isLoading}
              className='bg-primary disabled:bg-primary/70 h-[45px] w-[16.063rem] rounded-[5px] text-[15px] font-bold text-white'
            >
              {isLoading && (
                <span className='size-4 animate-spin rounded-full border-t border-r border-white max-lg:size-2' />
              )}
              {t('Confirm address')}
            </Button>
          </div>
        </div>
      )}
    </>
  );
};

export default AddEditAddressMap;
